import React, { useEffect, useState } from 'react'
import { Knower as KnowerInterface } from 'interfaces/Knower'

import { trackPromise } from 'react-promise-tracker'
import KnowerService from 'services/KnowerService'
import { ApiError } from 'services/ApiService'
import styled from 'styled-components'
import KnowService from 'services/KnowService'
import { Know } from 'interfaces/Know'
import InfiniteScroll from 'react-infinite-scroller'
import Spinner from 'components/elements/Spinner'
import { useRouter } from 'next/router'
import { useCallback } from 'react'
import Meta from 'components/modules/SEO/Meta'
import { GetServerSideProps } from 'next'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { getPathFromUrl, getPathWithOldQueryParams, parseUuidFromSlug } from 'lib/formatting/url'
import { useTranslation } from 'next-i18next'
import theme from 'stories/utils/theme'
import { Body1, Headline2 } from 'stories/elements/Typography/Text'
import { instagramURL, knowerProfileRoute, tiktokURL } from 'lib/constants/routes'
import { parseLocale } from 'lib/constants/localization'
import NothingFoundImage from 'assets/nothing_found.svg'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import FeedKnowCard from 'stories/elements/Know/FeedKnowCard'
import KnowerInfo from 'components/feature/UserProfile/KnowerInfo'
import Row from 'components/elements/Row'
import InstagramIcon from 'assets/footer/instagram.svg'
import TikTokIcon from 'assets/footer/tiktok.svg'
import Stack from 'components/elements/Stack'
import { trackEvent } from 'lib/tracking/analytics-service'
import { AnalyticsEventKnowerProfileKnowClick } from 'lib/constants/AnalyticsEventType'
import ReportDialog from 'components/feature/Reports/ReportDialog'
import { ReportType } from 'interfaces/UserReport'

const Container = styled.div`
    background-color: ${theme.colors.white};
    position: relative;
    padding-top: 50px;
    .banner {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        background-color: ${theme.colors.deepSeaBlue};
        height: 300px;
        z-index: 0;
        @media (max-width: 750px) {
            height: 250px;
        }
    }
    @media (max-width: 750px) {
        padding: 0;
    }
`

const Content = styled.div`
    z-index: 2;
    display: grid;
    row-gap: 20px;
    padding: 25px 5px;
    background-color: ${theme.colors.white};
`

const NotFoundContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100vh;
    gap: 10px;
`

const Header = styled.div`
    z-index: 1;
    background-color: ${theme.colors.white};
    border-radius: ${theme.borderRadius.card};
    position: relative;
    padding: 25px;
    box-shadow: 5px 13px 45px rgb(7 7 7 / 8%);
    .social path {
        fill: ${theme.colors.actionGray};
    }
    .report {
        position: absolute;
        top: 15px;
        right: 15px;
    }
`

const KnowGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 25px;
    @media (max-width: 950px) {
        grid-template-columns: 1fr;
    }
`

const loadingAreas = {
    knower: 'knower',
    knows: 'knowerKnows',
}

interface Props {
    knower: KnowerInterface | null
    knows: Know[] | null
    nextCursor: string | null
}

function Knower({ knower, knows: initialKnows, nextCursor }: Props) {
    const router = useRouter()
    const { slug } = router.query
    const [knowerUuid, setKnowerUuid] = useState<string | null>(null)
    const [knows, setKnows] = useState<Know[]>(initialKnows ?? [])
    const [cursor, setCursor] = useState<string | undefined>(nextCursor ?? undefined)

    const { t } = useTranslation('knowers')
    const { t: tCommon } = useTranslation('common')

    const loadKnows = useCallback(
        (knowerUuid: string, pageNumber: number, cursor?: string) => {
            if (pageNumber === 0 && initialKnows) return

            const promise = KnowService.list(knowerUuid, pageNumber, cursor)
                .then((response) => {
                    setKnows((k) => [...k, ...response.knows])
                    setCursor(response.nextCursor ?? null)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'know.list')
                    } else {
                        throw error
                    }
                })

            if (pageNumber === 1) {
                trackPromise(promise, loadingAreas.knows)
            }
        },
        [tCommon, initialKnows]
    )

    useEffect(() => {
        if (typeof slug !== 'string') {
            return
        }

        const knowerUuid = parseUuidFromSlug(slug)
        if (!knowerUuid) return
        setKnowerUuid(knowerUuid)
        loadKnows(knowerUuid, 0)
    }, [slug, loadKnows])

    const handleKnowerProfileKnowClick = (know?: Know) => {
        trackEvent(AnalyticsEventKnowerProfileKnowClick, {
            know: know,
            referrerScreen: 'knowerProfileKnows',
        })
    }

    const documentTitle = knower ? `${knower.user.name}` : 'Knower'

    const showKnowerSummaryText = knower?.user.grade?.longName && knower?.user.createdOn

    return (
        <Container>
            <section>
                <div className="banner" />
                <Meta
                    title={documentTitle}
                    noIndex={!knower?.isSeoIndexed}
                    description={t('knowers/pageDescription')}
                    profileUsername={knower?.user.username}
                />

                {knowerUuid && knower ? (
                    <Content>
                        <Header>
                            <div className="report">
                                <ReportDialog reportType={ReportType.user} reportedUserUuid={knower.user.uuid} />
                            </div>
                            <KnowerInfo knower={knower} user={knower.user} isHeading showGradeAndSubjects />
                            <Stack>
                                <span />
                                {showKnowerSummaryText ? (
                                    <Body1 fontFamily="var(--font-inter)" fontWeight={400}>
                                        {t('knowers/summaryText', {
                                            username: knower.user.username,
                                            grade: knower.user.grade?.longName,
                                            joinMonth: knower?.user.createdOn.split('/')[0],
                                            joinYear: knower?.user.createdOn.split('/').pop(),
                                        })}
                                    </Body1>
                                ) : null}

                                {knower.user?.instagramUsername ? (
                                    <a href={instagramURL(knower.user.instagramUsername)} target="_blank" rel="noreferrer">
                                        <Row>
                                            <InstagramIcon className="social" />
                                            <Body1
                                                fontFamily="var(--font-inter)"
                                                fontWeight={500}
                                                color={theme.colors.headlineDark}
                                            >
                                                {knower.user.instagramUsername}
                                            </Body1>
                                        </Row>
                                    </a>
                                ) : null}
                                {knower.user?.tiktokUsername ? (
                                    <a href={tiktokURL(knower.user.tiktokUsername)} target="_blank" rel="noreferrer">
                                        <Row>
                                            <TikTokIcon className="social" />
                                            <Body1
                                                fontFamily="var(--font-inter)"
                                                fontWeight={500}
                                                color={theme.colors.headlineDark}
                                            >
                                                {knower.user.tiktokUsername}
                                            </Body1>
                                        </Row>
                                    </a>
                                ) : null}
                            </Stack>
                        </Header>

                        <Headline2 fontSize={24} fontWeight={600} color={theme.colors.headlineDark}>
                            {t('knowers/uploadContentTitle', { count: knows.length, username: knower.user.name })}
                        </Headline2>

                        {knows.length === 0 ? <Body1>{t('knowers/emptyKnowsList')}</Body1> : null}

                        <InfiniteScroll
                            pageStart={0}
                            loadMore={(pageNumber) => loadKnows(knowerUuid, pageNumber, cursor)}
                            hasMore={!!cursor}
                            loader={<Spinner forceShow key={0}></Spinner>}
                            threshold={800}
                        >
                            <KnowGrid>
                                {knows.map((know) => (
                                    <FeedKnowCard
                                        onClick={handleKnowerProfileKnowClick}
                                        know={know}
                                        key={know.uuid}
                                        knowUuid={know.uuid}
                                    />
                                ))}
                            </KnowGrid>
                        </InfiniteScroll>

                        <Spinner area={loadingAreas.knows} />
                    </Content>
                ) : (
                    <NotFoundContainer>
                        <NothingFoundImage />
                        <Body1>{t('knowers/knowerNotFoundErrorMessage')}</Body1>
                    </NotFoundContainer>
                )}
            </section>
        </Container>
    )
}

export const getServerSideProps: GetServerSideProps<Props> = async (context) => {
    if (!context.params || typeof context.params.slug !== 'string') {
        return { notFound: true }
    }
    const translations = await serverSideTranslations(parseLocale(context.locale).interfaceLanguageCode, [
        'common',
        'knowers',
        'knows',
        'profile',
    ])

    const knowerIdentifier = parseUuidFromSlug(context.params.slug) as string
    if (knowerIdentifier === null) {
        return { notFound: true }
    }

    let knower: KnowerInterface
    let knows: Know[] | null = null
    let cursor: string | null = null

    try {
        knower = await KnowerService.get(knowerIdentifier)
    } catch (error) {
        if (error instanceof ApiError) {
            if (error.type === ErrorTypes.NotFound) {
                return { notFound: true }
            } else {
                return {
                    props: {
                        user: null,
                        knower: null,
                        knows: null,
                        nextCursor: null,
                    },
                }
            }
        } else {
            throw error
        }
    }

    const knowsResponse = await KnowService.list(knower.uuid, 0)
    if (knowsResponse) {
        knows = knowsResponse.knows
        cursor = knowsResponse.nextCursor || null
    }

    if (getPathFromUrl(context.resolvedUrl) !== knowerProfileRoute(knower)) {
        return {
            redirect: {
                permanent: true,
                destination: getPathWithOldQueryParams(knowerProfileRoute(knower), context.resolvedUrl),
            },
        }
    }

    return {
        props: {
            knower,
            knows,
            nextCursor: cursor ?? null,
            ...translations,
        },
    }
}

export default Knower
